import { Component, Input, OnChanges, SimpleChange, SimpleChanges, OnDestroy } from '@angular/core';
import { ScheduledDiscovery, DiscoverySubtaskStatus, CollectorChainEntry } from 'DiscoveryEngine.TS/Discovery';
import { NetFacadeService } from 'netview';
import { ScheduledDiscoveryStatusDto, TaskState, DiscoveryPhase } from 'UvExplorerServer.Dtos.TS/Discovery';
import { MessageService } from 'base';
import { AgentDto } from 'UvExplorerServer.Dtos.TS/Agents';
import { DateService } from 'base';

@Component({
	selector: 'sched-disc-status',
	templateUrl: './sched-disc-status.component.html',
	styleUrls: ['./sched-disc-status.component.scss']
})
export class SchedDiscStatusComponent implements OnChanges, OnDestroy {

	@Input()
	public discovery: ScheduledDiscovery;
	@Input()
	public selected: boolean;

	private agent: AgentDto;
	private destroyed: boolean;
	private timerId: any;
	private status: ScheduledDiscoveryStatusDto;

	public state: string;
	public startTime: string;
	public endTime: string;
	public totalProgress: string;
	public discoveryPhase: string;
	public phaseProgress: string;
	public lastDevice: string;

	public allDevices: number;
	public deviceCountRows: any[];

	public subtaskStatusValues: SubtaskStatusViewModel[];

	constructor(private netFacadeService: NetFacadeService,
		private messageService: MessageService,
		private dateService: DateService) {

		this.discovery = null;
		this.selected = false;
		this.agent = null;
		this.destroyed = false;
		this.timerId = undefined;
		this.status = null;

		this.clearViewProperties();
	}

	private clearViewProperties(): void {
		this.state = "";
		this.startTime = "";
		this.endTime = "";
		this.totalProgress = "";
		this.discoveryPhase = "";
		this.phaseProgress = "";
		this.lastDevice = "";
		this.allDevices = 0;
		this.deviceCountRows = [];
		this.subtaskStatusValues = [];
	}

	private setViewProperties(): void {
		this.setState();
		this.setStartTime();
		this.setEndTime();
		this.setTotalProgress();
		this.setDiscoveryPhase();
		this.setPhaseProgress();
		this.setLastDevice();
		this.setAllDevices();
		this.setDeviceCountRows();
		this.setSubtaskStatusValues();
	}

	public setState(): void {
		let result = "";
		if (this.status) {
			result = TaskState[this.status.State];
			if (this.status.HasMessage) {
				result += ` (${this.status.Message})`;
			}
		}
		this.state = result;
	}

	public setStartTime(): void {
		let result = "";
		if (this.status && this.status.HasStartTime) {
			result = this.dateService.formatDateHm(this.status.StartTime);
		}
		this.startTime = result;
	}

	public setEndTime(): void {
		let result = "";
		if (this.status && this.status.HasEndTime) {
			result = this.dateService.formatDateHm(this.status.EndTime);
		}
		this.endTime = result;
	}

	public setTotalProgress(): void {
		let result = "";
		if (this.status && this.status.State == TaskState.Running && this.status.HasTotalProgress) {
			result = this.status.TotalProgress + "%";
		}
		this.totalProgress = result;
	}

	public setDiscoveryPhase(): void {
		let result = "";
		if (this.status && this.status.State == TaskState.Running) {
			result = DiscoveryPhase[this.status.DiscoveryPhase];
		}
		this.discoveryPhase = result;
	}

	public setPhaseProgress(): void {
		let result = "";
		if (this.status && this.status.State == TaskState.Running && this.status.HasPhaseProgress) {
			result = this.status.PhaseProgress + "%";
		}
		this.phaseProgress = result;
	}

	public setLastDevice(): void {
		let result = "";
		if (this.status && this.status.State == TaskState.Running && this.status.HasLastDevice) {
			result = this.status.LastDevice;
		}
		this.lastDevice = result;
	}

	public setAllDevices(): void {
		let result = 0;
		if (this.status) {
			result = this.status.DeviceCounts.AllDevices;
		}
		this.allDevices = result;
	}

	public setDeviceCountRows(): void {
		let result: any[] = [];
		if (this.status) {
			let devCounts = this.status.DeviceCounts;
			this.addDevCatCount(result, devCounts.Routers, "Router", "Routers", "images\\router.png");
			this.addDevCatCount(result, devCounts.Switches, "Switch", "Switches", "images\\switch.png");
			this.addDevCatCount(result, devCounts.Firewalls, "Firewall", "Firewalls", "images\\firewall.png");
			this.addDevCatCount(result, devCounts.WirelessAPs, "Wireless AP", "Wireless APs", "images\\wireless-ap.png");
			this.addDevCatCount(result, devCounts.WirelessControllers, "Wireless Controller", "Wireless Controllers", "images\\wireless-controller.png");
			this.addDevCatCount(result, devCounts.OtherNetDevices, "Other Net Device", "Other Net Devices", "images\\hub.png");
			this.addDevCatCount(result, devCounts.Printers, "Printer", "Printers", "images\\printer.png");
			this.addDevCatCount(result, devCounts.Servers, "Server", "Servers", "images\\server.png");
			this.addDevCatCount(result, devCounts.Workstations, "Workstation", "Workstations", "images\\workstation.png");
			this.addDevCatCount(result, devCounts.IpPhones, "IP Phone", "IP Phones", "images\\phone.png");
			this.addDevCatCount(result, devCounts.IpPhoneManagers, "IP Phone Manager", "IP Phone Managers", "images\\phone-manager.png");
			this.addDevCatCount(result, devCounts.OtherDevices, "Other Device", "Other Devices", "images\\unknown.png");
		}
		this.deviceCountRows = result;
	}

	public setSubtaskStatusValues(): void {
		let result: SubtaskStatusViewModel[] = [];
		if (this.status && this.status.HasSubtaskStatus) {
			this.status.SubtaskStatus.ForEach((subtaskStatus) => {
				result.push(new SubtaskStatusViewModel(subtaskStatus));
			});
		}
		this.subtaskStatusValues = result;
	}

	private addDevCatCount(deviceCountRows: any[], devCatCount: number, singularDevCatLabel: string,
		pluralDevCatLabel: string, imageLink: string): void {

		const DevCatsPerRow: number = 4;

		if (devCatCount > 0) {

			let lastRow: any[] = null;
			if (deviceCountRows.length > 0) {
				lastRow = deviceCountRows[deviceCountRows.length - 1];
			}
			if (lastRow == null || lastRow.length == DevCatsPerRow) {
				lastRow = [];
				deviceCountRows.push(lastRow);
			}

			let devCatLabel: string = devCatCount + " " + ((devCatCount == 1) ? singularDevCatLabel : pluralDevCatLabel);

			lastRow.push({
				label: devCatLabel,
				image: imageLink
			});
		}
	}

	public ngOnChanges(changes: SimpleChanges): void {

		if (this.destroyed)
			return;

		if (changes["discovery"]) {
			this.stopTimer();
			this.agent = null;
			this.status = null;
			this.clearViewProperties();

			if (this.discovery) {
				this.agent = this.netFacadeService.findSchedDiscParent(this.discovery.GUID);
				this.fetchStatus();
				this.startTimer();
			}
		}
		else if (changes["selected"]) {
			this.stopTimer();
			this.startTimer();
		}
	}

	private startTimer(): void {
		if (!this.timerId && this.selected &&
			this.agent && this.agent.DiscoveryStatusInterval > 0) {

			let timeoutMillis = this.agent.DiscoveryStatusInterval * 1000;
			this.timerId = setInterval(() => {
				this.fetchStatus();
			}, timeoutMillis);
		}
	}

	private stopTimer(): void {
		if (this.timerId) {
			clearInterval(this.timerId);
			this.timerId = undefined;
		}
	}

	public ngOnDestroy(): void {
		this.destroyed = true;
		this.stopTimer();
	}

	private fetchStatus(): void {
		if (this.discovery) {
			this.netFacadeService.getSchedDiscStatus(this.discovery.GUID).subscribe(
				(result: ScheduledDiscoveryStatusDto) => {
					if (result) {
						this.status = result;
						this.setViewProperties();
					}
					else {
						this.status = null;
						this.clearViewProperties();
					}
				},
				(error: any) => {
					console.error(error);
				}
			);
		}
	}

	public updateStatus(): void {
		this.fetchStatus();
	}

	public get isDiscoveryRunning(): boolean {
		return (this.status && this.status.State == TaskState.Running && this.status.HasDiscTaskHandle);
	}

	public cancelSubtask(row: SubtaskStatusViewModel): void {
		if (this.agent && this.status.HasDiscTaskHandle && row.subtaskStatus.HasSubtaskHandle) {
			this.netFacadeService.cancelSubtaskAsap(this.agent, this.status.DiscTaskHandle, row.subtaskStatus.SubtaskHandle).subscribe(
				(result: any) => {
					this.messageService.displayInfoMessage("Task cancellation requested.");
				},
				(error: any) => {
					console.error(error);
					this.messageService.displayErrorMessage("Error requesting task cancellation.");
				}
			);
		}
	}

	public cancelDiscovery(): void {
		if (this.agent && this.status && this.status.HasDiscTaskHandle) {
			this.netFacadeService.cancelDiscoveryAsap(this.agent, this.status.DiscTaskHandle).subscribe(
				(result: any) => {
					this.messageService.displayInfoMessage("Discovery cancellation requested.");
				},
				(error: any) => {
					console.error(error);
					this.messageService.displayErrorMessage("Error requesting discovery cancellation.");
				}
			);
		}
	}

}

export class CollectorChainEntryViewModel {
	public CollectorType: string;
	public InfoSetName: string;
	public RunTime: string;

	constructor(entry: CollectorChainEntry) {
		this.CollectorType = entry.CollectorType;
		this.InfoSetName = entry.InfoSetName;
		this.RunTime = entry.HasRunTime ? entry.RunTime.ToStringNoFraction() : "";
	}
}

export class SubtaskStatusViewModel {
	public IpAddress: string;
	public TaskType: string;
	public RunTime: string;
	public CollectorChainValues: CollectorChainEntryViewModel[];

	public constructor(public subtaskStatus: DiscoverySubtaskStatus) {
		this.IpAddress = subtaskStatus.HasIpAddress ? subtaskStatus.IpAddress.ToString() : "";
		this.TaskType = subtaskStatus.HasTaskType ? subtaskStatus.TaskType : "";
		this.RunTime = subtaskStatus.HasRunTime ? subtaskStatus.RunTime.ToStringNoFraction() : "";
		this.CollectorChainValues = [];
		if (subtaskStatus.HasCollectorChain) {
			subtaskStatus.CollectorChain.ForEach((entry) => {
				this.CollectorChainValues.push(new CollectorChainEntryViewModel(entry));
			});
		}
	}
}
